import { HoverCard } from "#components/ui/HoverCard.tsx";
import { HoverCardPageLink } from "#components/ui/HoverCardPageLink.tsx";
import { SkillIcon } from "#features/conversations/components/conversation-input/SkillIcon.tsx";
import { TokenWrittenAs } from "#features/conversations/components/conversation-input/TokenWrittenAs.tsx";
import { useHoverCard } from "#hooks/useHoverCard.ts";
import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import { Div } from "@hopper-ui/components";
import { useId, useRef } from "react";
import { Button } from "react-aria-components";
import "./SkillToken.css";

interface SkillCard {
    description: string;
    // Closes the card and keeps it shut, as the composer does while the user types or a tray is open.
    isDisabled?: boolean;
    // A sent message's token is a button that only opens its card, as a citation pill with no link is: keyboard focus
    // or a tap opens it. The composer's caret can't land on a token, so there it opens on hover alone.
    isPressable?: boolean;
    // The skill's page, which the card links to in a new tab; the token itself only opens the card.
    pageHref?: string;
}

interface SkillTokenProps {
    title: string;
    // The command a sent message holds ("/summarize"), which a copy of the message writes in place of the title.
    writtenAs?: string;
    // What hovering the token explains; without it the token is only its icon and title.
    card?: SkillCard;
}

const tokenClassName = (writtenAs: string | undefined) => (writtenAs ? "wai-skill-token wai-token--copies-written" : "wai-skill-token");

const SkillTokenWithCard = ({ title, writtenAs, description, isDisabled = false, isPressable = false, pageHref }: SkillCard & Omit<SkillTokenProps, "card">) => {
    const { tShared } = useSharedTranslation();
    const spanRef = useRef<HTMLSpanElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const triggerRef = isPressable ? buttonRef : spanRef;
    const cardRef = useRef<HTMLElement>(null);
    const cardId = useId();
    const { isOpen, triggerProps, popoverProps, cardProps } = useHoverCard({ triggerRef, cardRef, isDisabled, opensOnPress: isPressable });
    const className = tokenClassName(writtenAs);
    const content = (
        <>
            {writtenAs && <TokenWrittenAs text={writtenAs} />}
            <SkillIcon className="wai-skill-token__icon" />
            {title}
        </>
    );

    return (
        <>
            {isPressable
                ? (
                    <Button ref={buttonRef} className={className} aria-describedby={isOpen ? cardId : undefined} {...triggerProps}>
                        {content}
                    </Button>
                )
                : (
                    <span ref={spanRef} className={className} onPointerMove={triggerProps.onPointerMove} onPointerLeave={triggerProps.onPointerLeave}>
                        {content}
                    </span>
                )}
            <HoverCard triggerRef={triggerRef} cardRef={cardRef} cardId={cardId} label={title} className="wai-skill-hover-card" popoverProps={popoverProps} cardProps={cardProps}>
                <p className="wai-skill-hover-card__title">
                    <SkillIcon className="wai-skill-hover-card__icon" />
                    {title}
                </p>
                {description && <p className="wai-skill-hover-card__description">{description}</p>}
                {pageHref && (
                    // Spaced by Hopper rather than this card's CSS, which a host may still serve in an older version.
                    <Div marginTop="stack-sm">
                        <HoverCardPageLink href={pageHref}>{tShared("viewSkill")}</HoverCardPageLink>
                    </Div>
                )}
            </HoverCard>
        </>
    );
};

// A skill as the composer and a sent message both show it, beside MentionToken; it takes the colour of the text around it.
export const SkillToken = ({ title, writtenAs, card }: SkillTokenProps) => (card
    ? <SkillTokenWithCard title={title} writtenAs={writtenAs} {...card} />
    : (
        <span className={tokenClassName(writtenAs)}>
            {writtenAs && <TokenWrittenAs text={writtenAs} />}
            <SkillIcon className="wai-skill-token__icon" />
            {title}
        </span>
    ));
